/**
 * Utility for creating and managing SSE connections  
 * Handles authentication, retry logic, and connection stability
 */

/**
 * Configuration options for SSE connections
 */
export interface SSEConnectionOptions {
  /**
   * Base URL for the SSE endpoint
   */
  url: string;

  /**
   * Whether to add a timestamp to prevent caching
   */
  preventCache?: boolean;

  /**
   * Maximum number of retry attempts
   */
  maxRetries?: number;

  /**
   * Initial delay for retry in milliseconds
   */
  initialRetryDelay?: number;

  /**
   * Maximum delay for retry in milliseconds
   */
  maxRetryDelay?: number;

  /**
   * Callback when connection is established
   */
  onOpen?: () => void;

  /**
   * Callback when connection encounters an error
   */
  onError?: (event: Event, retryCount: number, willRetry: boolean) => void;

  /**
   * Callback when max retries are reached
   */
  onMaxRetriesReached?: () => void;
}

/**
 * State of an SSE connection
 */
export interface SSEConnectionState {
  isConnected: boolean;
  retryCount: number;
  lastError: Event | null;
  eventSource: EventSource | null;
}

/**
 * Adds authentication token to SSE URL
 * Adds token from localStorage to support both normal and incognito modes
 */
export function addAuthTokenToUrl(url: string): string {
  try {
    const getCookie = (name: string): string | null => {
      if (typeof document === 'undefined') {
        return null;
      }

      const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
      return match ? decodeURIComponent(match[1]) : null;
    };

    const accessToken =
      localStorage.getItem('access_token') ??
      localStorage.getItem('accessToken') ??
      getCookie('access_token');

    if (accessToken) {
      const separator = url.includes('?') ? '&' : '?';
      // Add token as query parameter for SSE authentication
      return `${url}${separator}token=${encodeURIComponent(accessToken)}`;
    }

    return url;
  } catch (error) {
    return url;
  }
}

/**
 * Remove o token da URL antes de ela ir para log.
 *
 * `addAuthTokenToUrl` põe o access token como query param porque EventSource
 * não aceita header. Logar a URL crua — inclusive o `eventSource.url`, que já
 * a carrega — publica a credencial no console do navegador.
 */
export function redactTokenInUrl(url: string): string {
  return url.replace(/([?&]token=)[^&]+/g, '$1***');
}

/**
 * Creates an SSE connection with enhanced error handling and retry logic
 * Uses localStorage tokens for authentication in all modes
 */
export class SSEConnection {
  private options: Required<SSEConnectionOptions>;
  private state: SSEConnectionState;
  private reconnectTimeout: NodeJS.Timeout | null = null;
  private isManuallyDisconnected = false;

  constructor(options: SSEConnectionOptions) {
    this.options = {
      preventCache: true,
      maxRetries: 5,
      initialRetryDelay: 1000,
      maxRetryDelay: 30000,
      onOpen: () => { },
      onError: () => { },
      onMaxRetriesReached: () => { },
      ...options
    };

    this.state = {
      isConnected: false,
      retryCount: 0,
      lastError: null,
      eventSource: null
    };
  }

  /**
   * Builds the final URL with all necessary parameters
   */
  private buildUrl(): string {
    let url = this.options.url;

    // Add timestamp to prevent caching
    if (this.options.preventCache) {
      const separator = url.includes('?') ? '&' : '?';
      url = `${url}${separator}_t=${Date.now()}`;
    }

    // Add auth token for authentication
    url = addAuthTokenToUrl(url);

    return url;
  }

  /**
   * Calculates the retry delay using exponential backoff
   */
  private calculateRetryDelay(): number {
    const exponentialDelay = this.options.initialRetryDelay * Math.pow(2, this.state.retryCount);
    return Math.min(exponentialDelay, this.options.maxRetryDelay);
  }

  /**
   * Connects to the SSE endpoint
   */
  connect(): EventSource {
    // Clear any existing connection
    this.disconnect(false);

    this.isManuallyDisconnected = false;

    const url = this.buildUrl();

    const eventSource = new EventSource(url);

    this.state.eventSource = eventSource;

    // Setup event handlers
    eventSource.onopen = () => {
      this.state.isConnected = true;
      this.state.retryCount = 0;
      this.state.lastError = null;
      this.options.onOpen();
    };

    eventSource.onerror = (event) => {
      this.state.isConnected = false;
      this.state.lastError = event;

      // Don't retry if manually disconnected
      if (this.isManuallyDisconnected) {
        return;
      }

      // Check if we should retry
      const shouldRetry = this.state.retryCount < this.options.maxRetries;

      // Call error callback
      this.options.onError(event, this.state.retryCount, shouldRetry);

      // Only retry if connection is closed and we haven't exceeded max retries
      if (eventSource.readyState === EventSource.CLOSED && shouldRetry) {
        this.state.retryCount++;
        const delay = this.calculateRetryDelay();

        this.reconnectTimeout = setTimeout(() => {
          this.connect();
        }, delay);
      } else if (this.state.retryCount >= this.options.maxRetries) {
        this.options.onMaxRetriesReached();
      }
    };

    return eventSource;
  }

  /**
   * Disconnects from the SSE endpoint
   */
  disconnect(manual: boolean = true): void {
    this.isManuallyDisconnected = manual;

    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }

    if (this.state.eventSource) {
      this.state.eventSource.close();
      this.state.eventSource = null;
    }

    this.state.isConnected = false;
  }

  /**
   * Manually triggers a reconnection (resets retry count)
   */
  reconnect(): EventSource {
    this.state.retryCount = 0;
    this.state.lastError = null;
    return this.connect();
  }

  /**
   * Gets the current connection state
   */
  getState(): Readonly<SSEConnectionState> {
    return { ...this.state };
  }

  /**
   * Gets the EventSource instance
   */
  getEventSource(): EventSource | null {
    return this.state.eventSource;
  }
}

/**
 * Creates a simple SSE connection with default options
 * This is a convenience function for basic use cases
 */
export function createSSEConnection(
  url: string,
  options?: Partial<SSEConnectionOptions>
): SSEConnection {
  const connection = new SSEConnection({
    url,
    ...options
  });

  connection.connect();

  return connection;
}
